import { ChangeEvent, useRef, useState } from "react";
import { ImageOff, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { OnboardingSlot, uploadOnboardingImage } from "@/services/branding";

interface OnboardingImageUploaderProps {
  label: string;
  hint: string;
  slot: OnboardingSlot;
  currentUrl: string | null;
  onChange: (url: string | null) => void;
}

/** Portrait preview + upload/remove for one customer-app first-run image. */
export const OnboardingImageUploader = ({
  label,
  hint,
  slot,
  currentUrl,
  onChange,
}: OnboardingImageUploaderProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const onSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploading(true);
      const url = await uploadOnboardingImage(file, slot);
      onChange(url);
      toast.success(`${label} uploaded`);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="flex gap-4">
      <div className="h-32 w-24 shrink-0 overflow-hidden rounded-lg border bg-muted">
        {currentUrl ? (
          <img
            src={currentUrl}
            alt={label}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-muted-foreground">
            <ImageOff className="h-5 w-5" />
            <span className="text-[10px]">App default</span>
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{label}</p>
        <p className="text-xs text-muted-foreground">{hint}</p>
        <div className="mt-2 flex flex-wrap gap-2">
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={onSelect}
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
          >
            <Upload className="mr-2 h-4 w-4" />
            {uploading ? "Uploading…" : currentUrl ? "Replace" : "Upload"}
          </Button>
          {currentUrl ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => onChange(null)}
              disabled={uploading}
            >
              <X className="mr-2 h-4 w-4" />
              Use app default
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
};
